import React, { useEffect, useState, useRef, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import './notification.css';
import { useDispatch, useSelector } from "react-redux";
import { sendNotification, getUserData, scheduleNotification } from "../../Redux/Actions/notificationActions";
import { getProjectList } from "../../Redux/Actions/projectActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { getEventList } from "../../Redux/Actions/eventActions";
import { getNewsList } from "../../Redux/Actions/newsAction";
import { viewBuilderList } from "../../Redux/Actions/builderActions";
import { listRewardVouchers } from "../../Redux/Actions/rewardsAction";
import SelectInput from "../../Global Functions/selectInput";
import { viewCountryListPagination, viewCityListPagination, viewStateListPagination } from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import { AddImageAll } from "../../Global Functions/addImage";

const USER_TYPE_OPTIONS = ["user", "consultant", "builder", "exclusive partner"].map(o => ({ label: o, value: o }));

const NOTIFICATION_CATEGORIES = [
    { label: "none", value: "none" },
    { label: "Project", value: "project" },
    { label: "Rewards", value: "rewards" },
    { label: "Builder", value: "builder" },
    { label: "News", value: "news" },
    { label: "Events", value: "events" },
];

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const INITIAL_ENTITY_STATE = {
    notificationCategory: "",
    notificationEntityId: "",
    selectedProject: null,
    selectedEvent: null,
    selectedNews: null,
    selectedReward: null,
    selectedBuilder: null,
};

const INITIAL_FORM_STATE = {
    type: "",
    title: "",
    message: "",
    recipient: "",
    country: "",
    state: "",
    city: "",
    imgResponseUrl: "",
    scheduleDateTime: null,
    searchTerm: "",
    selectedUsers: [],
};

const CheckIcon = () => (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="2 6 5 9 10 3" />
    </svg>
);

const ConfirmModal = ({ onConfirm, onCancel }) => (
    <div className="ntf-modal-overlay">
        <div className="ntf-modal">
            <div className="ntf-modal-badge ntf-modal-badge--send">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
            </div>
            <h3>Send Notification</h3>
            <p>You're about to dispatch this notification to the selected recipients. This cannot be undone.</p>
            <div className="ntf-modal-btns">
                <button onClick={onCancel} className="ntf-btn ntf-btn-ghost">Cancel</button>
                <button onClick={onConfirm} className="ntf-btn ntf-btn-accent">Send Now</button>
            </div>
        </div>
    </div>
);

const ScheduleModal = ({ onClose, onSchedule }) => {
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const today = new Date().toISOString().split('T')[0];

    const handleSubmit = () => {
        if (!date || !time) { alert("Please select both date and time"); return; }
        onSchedule({ date, time });
        onClose();
    };

    return (
        <div className="ntf-modal-overlay">
            <div className="ntf-modal">
                <div className="ntf-modal-badge ntf-modal-badge--schedule">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                </div>
                <h3>Schedule Notification</h3>
                <p>Pick a date and time to automatically send this notification to your recipients.</p>
                <div className="ntf-modal-form">
                    <div className="ntf-modal-cols">
                        <div className="ntf-modal-field">
                            <label>Date</label>
                            <input type="date" value={date} min={today} onChange={e => setDate(e.target.value)} />
                        </div>
                        <div className="ntf-modal-field">
                            <label>Time</label>
                            <input type="time" value={time} onChange={e => setTime(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="ntf-modal-btns">
                    <button onClick={onClose} className="ntf-btn ntf-btn-ghost">Cancel</button>
                    <button onClick={handleSubmit} className="ntf-btn ntf-btn-accent">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                        </svg>
                        Confirm Schedule
                    </button>
                </div>
            </div>
        </div>
    );
};

const combineToUTCISO = (date, time) => new Date(`${date}T${time}:00`).toISOString();

const toSelectOptions = (arr, labelKey, valueKey = "_id") => {
    if (!Array.isArray(arr)) return [];
    return arr.map(item => ({ label: item[labelKey], value: item[valueKey] }));
};

const Notification = () => {
    const dispatch = useDispatch();

    const [form, setForm] = useState(INITIAL_FORM_STATE);
    const { type, title, message, recipient, country, state, city, imgResponseUrl, scheduleDateTime, searchTerm, selectedUsers } = form;

    const setFormField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const [entity, setEntity] = useState(INITIAL_ENTITY_STATE);
    const { notificationCategory, notificationEntityId, selectedProject, selectedEvent, selectedNews, selectedReward, selectedBuilder } = entity;

    const setEntityField = (updates) => setEntity(prev => ({ ...prev, ...updates }));

    const [allUsers, setAllUsers] = useState([]);
    const [userPage, setUserPage] = useState(1);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [sendPayload, setSendPayload] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [projectTypeFilter, setProjectTypeFilter] = useState("");
    const [enumsProjects, setEnumsProjects] = useState([]);
    const [projectPage] = useState(1);
    const projectLimit = 1150;

    const fileInputRef = useRef(null);

    const { projects, loading: projectLoading } = useSelector(s => s.projects);
    const { events, loading: eventLoading } = useSelector(s => s.events || {});
    const { builders, loading: builderLoading } = useSelector(s => s.builders);
    const { news, loading: newsLoading } = useSelector(s => s.news || {});
    const { vouchers = {}, loading: rewardLoading } = useSelector(s => s.rewards || {});
    const rewardsData = Array.isArray(vouchers?.data) ? vouchers.data : [];
    const { enumsData } = useSelector(s => s.enumsData);
    const { userData } = useSelector(s => s.notification || {});
    const { countryList } = useSelector(s => s.country || {});
    const { states } = useSelector(s => s.states || {});
    const cityState = useSelector(s => s.city || {});
    const loading = userData?.loading;

    const countryData = countryList?.data;
    const stateData = states?.data;
    const cityData = cityState?.citiesAllList?.data?.data;

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
    const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

    useEffect(() => {
        if (!enumsData?.data) dispatch(getEnums());
        if (!countryList?.data?.length) dispatch(viewCountryListPagination());
        if (!events?.data?.length) dispatch(getEventList({ page: 1, limit: 1000, is_deleted: false }));
        if (!news?.data?.length) dispatch(getNewsList({ page: 1, limit: 1000, is_deleted: false }));
        if (!vouchers?.data?.length) dispatch(listRewardVouchers({ page: 1, limit: 1000, is_deleted: false }));
        if (!builders?.data?.length) dispatch(viewBuilderList({ page: 1, limit: 1000, is_deleted: false }));
    }, [dispatch]);

    useEffect(() => {
        if (enumsData?.data?.ProjectStatus) {
            const statusEnums = Object.values(enumsData.data.ProjectStatus).filter(s => s !== "declined");
            if (statusEnums.length > 0) setEnumsProjects(statusEnums);
        }
    }, [enumsData]);

    const fetchProjects = useCallback(() => {
        if (!enumsProjects.length) return;
        dispatch(getProjectList({
            page: projectPage,
            limit: projectLimit,
            status: enumsProjects.filter(s => s !== "declined"),
            is_deleted: false,
            ...(projectTypeFilter && !["published", "unpublished"].includes(projectTypeFilter) && { name_withoutspace: projectTypeFilter }),
            ...(projectTypeFilter && !["residential", "commercial"].includes(projectTypeFilter) && { status: projectTypeFilter }),
        }));
    }, [dispatch, enumsProjects, projectPage, projectLimit, projectTypeFilter, projects?.data?.length]);

    useEffect(() => { fetchProjects(); }, [fetchProjects]);

    useEffect(() => {
        if (country) dispatch(viewStateListPagination({ country, page: 1, limit: 1000 }));
    }, [dispatch, country]);

    useEffect(() => {
        if (state && country) dispatch(viewCityListPagination({ state, country, page: 1, limit: 1000 }));
    }, [dispatch, state, country]);

    const handleCountryChange = (e) => {
        setFormField("country", e.target.value);
        setFormField("state", "");
        setFormField("city", "");
    };

    const handleStateChange = (e) => {
        setFormField("state", e.target.value);
        setFormField("city", "");
    };

    const fetchUsers = useCallback(() => {
        if (!type || !country || loading) return;
        dispatch(getUserData({
            list_for: type,
            is_deleted: false,
            page: 1,
            limit: 5000,
            country_id: country,
            state_id: state,
            city_id: city,
            filter: searchTerm,
        }));
    }, [dispatch, type, country, state, city, searchTerm, loading]);

    useEffect(() => {
        setAllUsers([]);
        setUserPage(1);
        setHasMoreUsers(true);
    }, []);

    useEffect(() => {
        if (type && country && recipient === 'selected') {
            setAllUsers([]);
            setUserPage(1);
            setHasMoreUsers(true);
            fetchUsers();
        }
    }, [type, country, recipient, fetchUsers]);

    useEffect(() => {
        if (userData?.data?.length > 0) {
            setAllUsers(prev => {
                const existingUids = new Set(prev.map(u => u.uid));
                const newUsers = userData.data
                    .filter(user => !existingUids.has(user._id))
                    .map(user => ({ name: user?.name, uid: user?._id, uids: user?.id }));
                return [...prev, ...newUsers];
            });
            setHasMoreUsers(false); 
        } else if (userData?.data?.length === 0 ) {
            setHasMoreUsers(false);
        }
    }, [userData, userPage]);

    const handleTypeChange = (e) => {
        setFormField("type", e.target.value);
        setFormField("state", "");
        setFormField("city", "");
        setProjectTypeFilter("");
        setEntityField(INITIAL_ENTITY_STATE);
    };

    const handleRecipientChange = (value) =>
        setFormField("recipient", recipient === value ? '' : value);

    const handleUserCheck = (userId) =>
        setFormField("selectedUsers", selectedUsers.includes(userId)
            ? selectedUsers.filter(i => i !== userId)
            : [...selectedUsers, userId]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 5 && hasMoreUsers && !loading) {
            setUserPage(prev => prev + 1);
        }
    };

    const handleRemoveImage = () => {
        setFormField("imgResponseUrl", "");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleImageUpload = async (event) => {
        const uploadedFile = event.target.files[0];
        if (!uploadedFile) return;
        if (!ALLOWED_IMAGE_TYPES.includes(uploadedFile.type)) {
            toast.error("Only JPG, JPEG, PNG, WebP files are allowed!");
            event.target.value = "";
            return;
        }
        if (uploadedFile.type.startsWith("image/")) {
            const img = new Image();
            img.onload = async () => {
                if (Math.abs((img.width / img.height) - (1.6 / 0.9)) > 0.09) {
                    toast.error("Image must have a 16:9 aspect ratio.");
                    return;
                }
                const responseUrl = await AddImageAll(uploadedFile);
                if (responseUrl) setFormField("imgResponseUrl", responseUrl);
            };
            img.src = URL.createObjectURL(uploadedFile);
        } else {
            const responseUrl = await AddImageAll(uploadedFile);
            if (responseUrl) setFormField("imgResponseUrl", responseUrl);
        }
        event.target.value = "";
    };

    const handleCategoryChange = (e) => {
        setEntityField({ ...INITIAL_ENTITY_STATE, notificationCategory: e.target.value });
    };

    const getTypeData = useCallback(() => {
        if (notificationCategory === 'project' && selectedProject) {
            return {
                images: selectedProject.images?.map(img => img.url) || [],
                cover_photo: selectedProject.cover_photo,
                cover_photo_thumbnail: selectedProject.cover_photo_thumbnail,
                project_id: selectedProject.project_id,
                project_name: selectedProject.project_name,
                brand_name: selectedProject.brand_name,
                construction_status: selectedProject.construction_status,
            };
        }
        if (notificationCategory === 'events' && selectedEvent) {
            return {
                images: selectedEvent.cover_photo ? [selectedEvent.cover_photo] : [],
                cover_photo: selectedEvent.cover_photo,
                event_id: selectedEvent.event_id,
                event_name: selectedEvent.event_name,
                event_name_withoutspace: selectedEvent.event_name_withoutspace,
                organizer_id: selectedEvent.organizer_id,
                is_free: selectedEvent.is_free,
                is_remind_before: selectedEvent.is_remind_before,
                start_date: selectedEvent.start_date,
                start_time: selectedEvent.start_time,
                start_date_utc: selectedEvent.start_date_utc,
                end_date: selectedEvent.end_date,
                end_date_utc: selectedEvent.end_date_utc,
                end_time: selectedEvent.end_time,
                no_of_audience: selectedEvent.no_of_audience,
                about_event: selectedEvent.about_event,
                status: selectedEvent.status,
                location: selectedEvent.location,
            };
        }
        if (notificationCategory === 'news' && selectedNews) {
            return {
                images: selectedNews.image ? [selectedNews.image] : [],
                news_id: selectedNews.news_id,
                title: selectedNews.title,
                start_date: selectedNews.start_date,
                end_date: selectedNews.end_date,
                description: selectedNews.description,
                status: selectedNews.status,
                news_link: selectedNews.news_link,
                category: selectedNews.category,
                url: selectedNews.url,
            };
        }
        if (notificationCategory === 'rewards' && selectedReward) {
            return {
                images: selectedReward.image ? [selectedReward.image] : [],
                cover_photo: selectedReward.image,
                voucher: selectedReward.voucher,
                title: selectedReward.title,
                description: selectedReward.description,
                reward_points: selectedReward.reward_points,
                brochure: selectedReward.brochure,
                position: selectedReward.position,
                primary: selectedReward.primary,
                terms_and_conditions: selectedReward.terms_and_conditions,
                reward_for: selectedReward.reward_for,
                how_to_redeem: selectedReward.how_to_redeem,
                category: selectedReward.category,
                status: selectedReward.status,
                country: selectedReward.country,
            };
        }
        if (notificationCategory === 'builder' && selectedBuilder) {
            return {
                images: selectedBuilder.brand_logo ? [selectedBuilder.brand_logo] : [],
                cover_photo: selectedBuilder.brand_logo || selectedBuilder.profile_image,
                builder_id: selectedBuilder.builder_id,
                brand_name: selectedBuilder.brand_name,
                organization_name: selectedBuilder.organization_name,
                primary_contact_number: selectedBuilder.primary_contact_number,
                primary_country_code: selectedBuilder.primary_country_code,
                primary_email: selectedBuilder.primary_email,
                rera_id: selectedBuilder.rera_id,
                profile_image: selectedBuilder.profile_image,
                role: selectedBuilder.role,
                account_status: selectedBuilder.account_status,
                total_projects: selectedBuilder.total_projects,
                live_projects: selectedBuilder.live_projects,
            };
        }
        return { images: [], cover_photo: '', cover_photo_thumbnail: '' };
    }, [notificationCategory, selectedProject, selectedEvent, selectedNews, selectedReward, selectedBuilder]);

    const buildPayload = useCallback((overrides = {}) => {
        const base = {
            title,
            message,
            type_name: notificationCategory,
            type_id: notificationCategory === 'none' ? 'a1b2c3d4e5f678901234abcd' : notificationEntityId,
            type_data: getTypeData(),
            ...(scheduleDateTime ? { schedule_at: combineToUTCISO(scheduleDateTime.date, scheduleDateTime.time) } : {}),
            ...(imgResponseUrl ? { image: imgResponseUrl } : {}),
        };
        if (recipient === "selected") {
            base.notification_for = type || "";
            base.list = selectedUsers;
            base.type = "selected";
        } else if (recipient === "all") {
            base.type = "all";
            base.notification_for = type || "";
            base.list = [];
        }
        return { ...base, ...overrides };
    }, [title, message, notificationCategory, notificationEntityId, getTypeData, scheduleDateTime, imgResponseUrl, recipient, type, selectedUsers]);

    const resetForm = () => {
        setForm(INITIAL_FORM_STATE);
        setEntity(INITIAL_ENTITY_STATE);
        setProjectTypeFilter("");
        setAllUsers([]);
        setUserPage(1);
        setHasMoreUsers(true);
        setSendPayload(null);
    };

    const prepareAndShowConfirm = (e) => {
        e.preventDefault();
        setSendPayload(buildPayload());
        setShowConfirm(true);
    };

    const handleConfirmYes = async () => {
        setShowConfirm(false);
        if (!sendPayload) return;
        const res = await dispatch(sendNotification(sendPayload));
        setIsSending(false);
        if (res.success === true) {
            toast.success("Notification sent");
            // resetForm();
        } else {
            toast.error("Error sending notification");
        }
    };

    const handleConfirmNo = () => {
        setShowConfirm(false);
        setSendPayload(null);
    };

    const handleScheduleConfirm = async (datetime) => {
        setShowScheduleModal(false);
        const scheduleAt = combineToUTCISO(datetime.date, datetime.time);
        const payload = buildPayload({ schedule_at: scheduleAt });
        if (recipient === "all") delete payload.list;
        setIsSending(true);
        const res = await dispatch(scheduleNotification(payload));
        setIsSending(false);
        if (res.success) {
            toast.success(`Notification scheduled for ${datetime.date} at ${datetime.time}`);
            resetForm();
        } else {
            toast.error("Error scheduling notification");
        }
    };

    const isFormValid = () => {
        if (type === 'none') return true;
        if (!type || !country) return false;
        if (notificationCategory === 'none') return true;
        if (!notificationCategory || !notificationEntityId) return false;
        if ((type === "builder" || type === "consultant") && (!state || !city)) return false;
        if (!title?.trim() || !message?.trim()) return false;
        if (!recipient) return false;
        if (recipient === "selected" && selectedUsers.length === 0) return false;
        return true;
    };

    const entitySelectMap = {
        project: {
            options: toSelectOptions(projects?.data, "project_name"),
            onSelect: (id) => setEntityField({ notificationEntityId: id, selectedProject: projects?.data?.find(p => p._id === id) || null }),
            disabled: projectLoading || !projects?.data?.length,
            placeholder: "Select a project",
            label: "Project",
        },
        events: {
            options: toSelectOptions(events?.data, "event_name"),
            onSelect: (id) => setEntityField({ notificationEntityId: id, selectedEvent: events?.data?.find(e => e._id === id) || null }),
            disabled: eventLoading || !events?.data?.length,
            placeholder: "Select an event",
            label: "Event",
        },
        news: {
            options: toSelectOptions(news?.data, "title"),
            onSelect: (id) => setEntityField({ notificationEntityId: id, selectedNews: news?.data?.find(n => n._id === id) || null }),
            disabled: newsLoading || !news?.data?.length,
            placeholder: "Select news",
            label: "News",
        },
        builder: {
            options: toSelectOptions(builders?.data, "brand_name"),
            onSelect: (id) => setEntityField({ notificationEntityId: id, selectedBuilder: builders?.data?.find(b => b._id === id) || null }),
            disabled: builderLoading || !builders?.data?.length,
            placeholder: "Select a builder",
            label: "Builder",
        },
        rewards: {
            options: toSelectOptions(rewardsData, "title"),
            onSelect: (id) => setEntityField({ notificationEntityId: id, selectedReward: rewardsData.find(r => r._id === id) || null }),
            disabled: rewardLoading || rewardsData.length === 0,
            placeholder: "Select a reward",
            label: "Reward",
        },
    };

    const activeEntitySelect = entitySelectMap[notificationCategory];

    return (
        <div>
            <MainLayout>
                <div className="ntf-page">
                    <div className="ntf-topbar">
                        <div>
                            <h1 className="ntf-page-title">Notifications</h1>
                            <p className="ntf-page-sub">Compose and dispatch targeted messages to your users</p>
                        </div>
                        {canAdd && canEdit && (
                            <div className="ntf-actions">
                                <button
                                    className="ntf-btn ntf-btn-ghost"
                                    onClick={() => setShowScheduleModal(true)}
                                    disabled={!isFormValid()}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    Schedule
                                </button>
                                <button
                                    onClick={prepareAndShowConfirm}
                                    className="ntf-btn ntf-btn-accent"
                                    disabled={!isFormValid() || isSending}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                                    </svg>
                                    {isSending ? "Sending…" : "Send Now"}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="ntf-layout">
                        <div className="ntf-left">
                            <div className="ntf-card">
                                <div className="ntf-card-head">
                                    <div className="ntf-card-head-left">
                                        <div className="ntf-card-icon ntf-card-icon--purple">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="ntf-card-title">Targeting</div>
                                            <div className="ntf-card-desc">Define audience scope</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="ntf-card-body">
                                    <div className="ntf-row-2">
                                        <div className="ntf-field">
                                            <label className="ntf-label">User Type <span className="req">*</span></label>
                                            <div className="ntf-select-wrap">
                                                <SelectInput
                                                    required
                                                    name="type"
                                                    value={type}
                                                    placeholder="Select an option"
                                                    options={USER_TYPE_OPTIONS}
                                                    onChange={handleTypeChange}
                                                    className="ntf-select"
                                                />
                                            </div>
                                        </div>
                                        <div className="ntf-field">
                                            <label className="ntf-label">Country <span className="req">*</span></label>
                                            <div className="ntf-select-wrap">
                                                <SelectInput
                                                    required
                                                    name="country"
                                                    value={country}
                                                    placeholder="Select an option"
                                                    options={toSelectOptions(countryData, "country_name")}
                                                    onChange={handleCountryChange}
                                                    className="ntf-select"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {(type === "builder" || type === "consultant") && (
                                        <div className="ntf-row-2">
                                            <div className="ntf-field">
                                                <label className="ntf-label">State</label>
                                                <div className="ntf-select-wrap">
                                                    <SelectInput
                                                        disabled={!country}
                                                        required
                                                        name="State"
                                                        value={state}
                                                        placeholder="Select"
                                                        options={toSelectOptions(stateData, "state_name")}
                                                        onChange={handleStateChange}
                                                        className="ntf-select"
                                                    />
                                                </div>
                                            </div>
                                            <div className="ntf-field">
                                                <label className="ntf-label">City</label>
                                                <div className="ntf-select-wrap">
                                                    <SelectInput
                                                        disabled={!state}
                                                        required
                                                        name="City"
                                                        value={city}
                                                        placeholder="Select"
                                                        options={toSelectOptions(cityData, "city_name")}
                                                        onChange={(e) => setFormField("city", e.target.value)}
                                                        className="ntf-select"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="ntf-divider" />

                                    <div className="ntf-field">
                                        <label className="ntf-label">Notification Type <span className="req">*</span></label>
                                        <div className="ntf-select-wrap">
                                            <SelectInput
                                                required
                                                name="notificationCategory"
                                                value={notificationCategory}
                                                placeholder="Select notification type"
                                                options={NOTIFICATION_CATEGORIES}
                                                onChange={handleCategoryChange}
                                                className="ntf-select"
                                            />
                                        </div>
                                    </div>

                                    {activeEntitySelect && (
                                        <div className="ntf-field">
                                            <label className="ntf-label">{activeEntitySelect.label} <span className="req">*</span></label>
                                            <div className="ntf-select-wrap">
                                                <SelectInput
                                                    required
                                                    name="entityId"
                                                    value={notificationEntityId}
                                                    placeholder={activeEntitySelect.placeholder}
                                                    options={activeEntitySelect.options}
                                                    onChange={(e) => activeEntitySelect.onSelect(e.target.value)}
                                                    disabled={activeEntitySelect.disabled}
                                                    className="ntf-select"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="ntf-card">
                                <div className="ntf-card-head">
                                    <div className="ntf-card-head-left">
                                        <div className="ntf-card-icon ntf-card-icon--amber">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="ntf-card-title">Content</div>
                                            <div className="ntf-card-desc">Write your message</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="ntf-card-body">
                                    <div className="ntf-field">
                                        <label className="ntf-label">
                                            Title <span className="req">*</span>
                                            <span className="hint">Max 50</span>
                                        </label>
                                        <input
                                            className="ntf-input"
                                            type="text"
                                            placeholder="Enter notification title…"
                                            maxLength={50}
                                            value={title}
                                            onChange={(e) => setFormField("title", e.target.value.trimStart())}
                                        />
                                        <span className="ntf-char-count">{title.length} / 50</span>
                                    </div>

                                    <div className="ntf-field">
                                        <label className="ntf-label">
                                            Message <span className="req">*</span>
                                            <span className="hint">Max 150</span>
                                        </label>
                                        <textarea
                                            className="ntf-textarea"
                                            placeholder="Write the notification body…"
                                            maxLength={150}
                                            value={message}
                                            onChange={(e) => setFormField("message", e.target.value)}
                                        />
                                        <span className="ntf-char-count">{message.length} / 150</span>
                                    </div>

                                    <div className="ntf-field">
                                        <label className="ntf-label">
                                            Image <span className="req">*</span>
                                            <span className="hint">16:9 · JPG PNG WebP</span>
                                        </label>
                                        {imgResponseUrl ? (
                                            <div className="ntf-preview-wrap">
                                                <img src={imgResponseUrl} alt="Preview" className="ntf-preview-img" />
                                                <button className="ntf-preview-remove" onClick={handleRemoveImage} aria-label="Remove image">×</button>
                                            </div>
                                        ) : (
                                            <label className="ntf-upload-area" htmlFor="ntfImageFile">
                                                <div className="upload-icon">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                </div>
                                                <span className="upload-title">Click to upload image</span>
                                                <span className="upload-sub">Supports JPG, PNG, WebP · 16:9 ratio required</span>
                                            </label>
                                        )}
                                        <input
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleImageUpload}
                                            id="ntfImageFile"
                                            hidden
                                            type="file"
                                            ref={fileInputRef}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ntf-recipient-card">
                            <div className="ntf-card-head">
                                <div className="ntf-card-head-left">
                                    <div className="ntf-card-icon ntf-card-icon--green">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="ntf-card-title">Recipients</div>
                                        <div className="ntf-card-desc">Who gets this message</div>
                                    </div>
                                </div>
                            </div>
                            <div className="ntf-card-body">
                                <div className="ntf-options-list">
                                    <div
                                        className={`ntf-option-row ${recipient === 'all' ? 'ntf-option-row--active' : ''}`}
                                        onClick={() => handleRecipientChange("all")}
                                    >
                                        <div className="ntf-option-icon ntf-option-icon--all">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                                            </svg>
                                        </div>
                                        <div className="ntf-option-text">
                                            <strong>All Users</strong>
                                            <span>Broadcast to every user in selected country</span>
                                        </div>
                                        <div className="ntf-option-checkbox">
                                            <div className={`ntf-custom-check ${recipient === 'all' ? 'ntf-custom-check--checked' : ''}`}>
                                                <CheckIcon />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`ntf-option-row ${recipient === 'selected' ? 'ntf-option-row--active' : ''} ${(!type || !country) ? 'ntf-option-row--disabled' : ''}`}
                                        onClick={() => (!type || !country) ? null : handleRecipientChange("selected")}
                                    >
                                        <div className="ntf-option-icon ntf-option-icon--custom">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                                            </svg>
                                        </div>
                                        <div className="ntf-option-text">
                                            <strong>Custom Selection</strong>
                                            <span>Hand-pick specific users to notify</span>
                                        </div>
                                        <div className="ntf-option-checkbox">
                                            <div className={`ntf-custom-check ${recipient === 'selected' ? 'ntf-custom-check--checked' : ''}`}>
                                                <CheckIcon />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {(!type || !country) && (
                                    <div className="ntf-warning-chip">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        {!type ? 'Select a user type to enable custom selection' : 'Select a country to enable custom selection'}
                                    </div>
                                )}

                                {recipient === "selected" && type && country && (
                                    <div className="ntf-user-panel">
                                        <div className="ntf-user-search-bar">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                            </svg>
                                            <input
                                                type="text"
                                                placeholder="Search by name or UID…"
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setFormField("searchTerm", e.target.value);
                                                    setAllUsers([]);
                                                    setUserPage(1);
                                                }}
                                            />
                                        </div>
                                        <div className="ntf-user-list-header">
                                            <span>Name</span>
                                            <span>UID</span>
                                        </div>
                                        <ul className="ntf-user-list" onScroll={handleScroll}>
                                            {allUsers.length === 0 ? (
                                                <li>
                                                    <div className="ntf-empty-state">
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                                                        </svg>
                                                        {!type ? 'Select user type first' : !country ? 'Select country first' : 'No users found'}
                                                    </div>
                                                </li>
                                            ) : (
                                                allUsers.map(user => (
                                                    <li key={user.uid}>
                                                        <label className="ntf-user-item">
                                                            <input
                                                                type="checkbox"
                                                                value={user.uid}
                                                                onChange={() => handleUserCheck(user.uid)}
                                                                checked={selectedUsers.includes(user.uid)}
                                                            />
                                                            <div className="ntf-user-item-info">
                                                                <span className="user-name">{user.name}</span>
                                                                <span className="user-uid">{user.uids}</span>
                                                            </div>
                                                        </label>
                                                    </li>
                                                ))
                                            )}
                                            {loading && <li><div className="ntf-loading">Loading more users…</div></li>}
                                        </ul>
                                    </div>
                                )}

                                {recipient === "selected" && selectedUsers.length > 0 && (
                                    <div className="ntf-selected-badge">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {showScheduleModal && (
                        <ScheduleModal
                            onClose={() => setShowScheduleModal(false)}
                            onSchedule={handleScheduleConfirm}
                        />
                    )}
                    {showConfirm && (
                        <ConfirmModal
                            onConfirm={handleConfirmYes}
                            onCancel={handleConfirmNo}
                        />
                    )}
                </div>
            </MainLayout>
        </div>
    );
};

export default Notification;